/**
 * Durable store for the new-comment composer draft.
 *
 * The draft is written to localStorage as the user types, keyed by the viewing
 * identity and the record, so it survives a full page reload: the guarded
 * stale-chunk reload after a deploy, a backend outage, a browser crash or a
 * closed tab. A route/beforeunload guard cannot cover these, because a
 * programmatic reload does not ask the user.
 *
 * Only a draft with real text is stored. Posting or discarding the comment
 * empties the draft, which removes the entry. Entries older than
 * DRAFT_MAX_AGE_MS are dropped when read and when the store is pruned.
 */

export const COMMENT_DRAFT_PREFIX = "cloudomation.commentDraft.";
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Longer drafts are not stored (keeps one draft from filling the quota). */
export const DRAFT_MAX_CHARS = 100_000;

export interface StoredCommentDraft {
    body: string;
    assigneeIdentityId: string | null;
    replyToId: string | null;
    savedAt: number;
}

export const commentDraftKey = (identityId: string, recordId: string): string =>
    `${COMMENT_DRAFT_PREFIX}${identityId}.${recordId}`;

const isStoredDraft = (value: unknown): value is StoredCommentDraft => {
    if (!value || typeof value !== "object") return false;
    const draft = value as Record<string, unknown>;
    return (
        typeof draft.body === "string" &&
        typeof draft.savedAt === "number" &&
        (draft.assigneeIdentityId === null || typeof draft.assigneeIdentityId === "string") &&
        (draft.replyToId === null || typeof draft.replyToId === "string")
    );
};

export const readCommentDraft = (
    storageKey: string,
    now: number = Date.now(),
): StoredCommentDraft | null => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (
            !isStoredDraft(parsed) ||
            parsed.body.trim().length === 0 ||
            now - parsed.savedAt > DRAFT_MAX_AGE_MS
        ) {
            localStorage.removeItem(storageKey);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

/** Store the draft, or remove the entry when it holds no text. */
export const writeCommentDraft = (
    storageKey: string,
    draft: Omit<StoredCommentDraft, "savedAt">,
    now: number = Date.now(),
): void => {
    try {
        if (draft.body.trim().length === 0 || draft.body.length > DRAFT_MAX_CHARS) {
            localStorage.removeItem(storageKey);
            return;
        }
        localStorage.setItem(storageKey, JSON.stringify({ ...draft, savedAt: now }));
    } catch {
        /* storage full or unavailable (private mode): the draft stays in memory */
    }
};

/** Remove expired or unreadable drafts of every record. */
export const pruneCommentDrafts = (now: number = Date.now()): void => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key?.startsWith(COMMENT_DRAFT_PREFIX)) keys.push(key);
        }
        keys.forEach((key) => readCommentDraft(key, now));
    } catch {
        /* ignore storage errors */
    }
};
